'use client';

/**
 * Realtime Sync — Supabase Broadcast-based live data sync
 *
 * Uses Supabase Realtime Broadcast (not Postgres changes) so all connected
 * users in the same company receive instant state updates without a page refresh.
 *
 * Event types:
 *  - 'materials_update'   → full updated materials array *  -'purchases_update'→ full updated purchases array *  -'products_update'→ full updated products array *  -'finished_stock_update'→ full updated finishedProductStock array *  -'stock_adjustments_update' → full updated stockAdjustments array
 */

import { createClient } from '@/lib/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type SyncEventType =
  | 'materials_update' |'purchases_update' |'products_update' |'finished_stock_update' |'stock_adjustments_update';

export interface SyncPayload<T = unknown> {
  event: SyncEventType;
  data: T;
  senderId: string; // user id of the broadcaster — receivers skip their own broadcasts
}

/**
 * Creates (or reuses) a company-scoped Realtime channel.
 * Returns the channel instance.
 */
export function createCompanyChannel(companyId: string): RealtimeChannel {
  const supabase = createClient();
  return supabase.channel(`company:${companyId}`, {
    config: { broadcast: { self: false } }, // don't echo back to sender
  });
}

/**
 * Broadcast a data update to all other users in the company.
 */
export async function broadcastUpdate<T>(
  channel: RealtimeChannel,
  event: SyncEventType,
  data: T,
  senderId: string
): Promise<void> {
  try {
    await channel.send({
      type: 'broadcast',
      event,
      payload: { data, senderId } satisfies Omit<SyncPayload<T>, 'event'>,
    });
  } catch {
    // Non-fatal — local state is already updated
  }
}
